import React, { useMemo, useRef, Suspense, useState } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import { getDistroBrowserIncompatibility, safeParse } from "../../Util";
import { usePost } from "../../API";
import { DataTable, DateDisplay, Select, Icon, GroupByPanel, Button, useToast } from "@aidkitorg/component-library";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { useAsyncJobStatusBatch } from "../../Hooks/AsyncJobStatusCheck";
import { useAsyncInterval } from "../../Util";
import { useHistory } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import type { FieldOption, GroupByConfig } from "@aidkitorg/types/lib/admin-panel/filters";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));
const EMPTY_GENERATOR: survey.ConversationGenerator = { name: "", steps: [] };

type TraceConversationItem = {
  id: string;
  origin_id?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  run_id: string;
  run_status: string;
  branch_index: number;
};

type TraceConversationRow = {
  id: string;
  origin_id?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by?: string | null;
  run_id: string;
  run_status: string;
  branch_index?: number;
  row_type: "run" | "conversation";
  expandableRows?: TraceConversationRow[];
};

export function ConversationGenerationConfig() {
  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const { toast } = useToast();
  const listGenerators = usePost('/ai/trace_generation/list');
  const registerGenerator = usePost('/ai/trace_generation/register');
  const queueTrace = usePost('/ai/trace_generation/queue');
  const listConversations = usePost('/ai/trace_generation/conversations');
  const getTraceSummary = usePost('/ai/trace_generation/summary');
  const getPlan = usePost('/ai/trace_generation/plan');
  const getDynamicPlan = usePost('/ai/trace_generation/dynamic_plan');
  const getInteractionsCSV = usePost('/ai/trace_generation/interactions_csv', { textOnly: true });

  const [generator, setGenerator] = useState<survey.ConversationGenerator>(EMPTY_GENERATOR);

  const [generatorList, setGeneratorList] = useState<survey.ConversationGenerator[]>([]);
  const [traceConversations, setTraceConversations] = useState<TraceConversationItem[]>([]);
  const [traceConversationsLoading, setTraceConversationsLoading] = useState(false);
  const [traceConversationsError, setTraceConversationsError] = useState<string | null>(null);
  const [groupByConfig, setGroupByConfig] = useState<GroupByConfig>({
    enabled: true,
    rules: [{ field: "run_id", direction: "desc" }]
  });
  const [traceSummary, setTraceSummary] = useState<Record<string, number>>({});
  const [traceSummaryError, setTraceSummaryError] = useState<string | null>(null);
  const history = useHistory();
  const { addTaskIds, clearTaskIds, hasActiveTasks, activeTaskIds } = useAsyncJobStatusBatch(3_000);
  const [plan, setPlan] = useState<number>();
  
  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);

  const generatorReferences = useMemo(() => generatorList
    .filter(g => g.name !== generator.name)
    .map(g => g.name),
  [generatorList, generator]);

  useAsyncEffect(async () => {
    setPlan(await getDynamicPlan({ steps: generator.steps }));
  }, [generator]);

  useAsyncEffect(async () => {
    const generators = await listGenerators({});

    setGeneratorList(
      generators.map(({ id, config: gen }) => ({
        id,
        name: gen.name!,
        steps: gen.steps
      }))
    );

    distroRef.current?.initialize(
      generator, undefined, undefined, undefined, { generators: generators.map(g => g.config.name) }
    );
  }, []);

  useAsyncEffect(async (signal) => {
    if (!generator.id) {
      setTraceConversations([]);
      setTraceConversationsError(null);
      setTraceSummary({});
      setTraceSummaryError(null);
      clearTaskIds();
      setPlan(0);
      return;
    }

    setPlan(await getPlan({
      scenarioId: generator.id
    }));

    setTraceConversationsLoading(true);
    setTraceConversationsError(null);
    try {
      const [conversationsResponse, summaryResponse] = await Promise.all([
        listConversations({ scenarioId: generator.id, limit: 50 }, signal),
        getTraceSummary({ scenarioId: generator.id }, signal)
      ]);
      if (!signal.aborted) {
        setTraceConversations(conversationsResponse.items ?? []);
        setTraceSummary(summaryResponse.counts ?? {});
        setTraceSummaryError(null);
      }
    } catch (error) {
      if (!signal.aborted) {
        setTraceConversationsError("Failed to load conversations.");
        setTraceConversations([]);
        setTraceSummaryError("Failed to load summary.");
        setTraceSummary({});
      }
    } finally {
      if (!signal.aborted) {
        setTraceConversationsLoading(false);
      }
    }
  }, [generator.id, listConversations, getTraceSummary, clearTaskIds]);

  useAsyncInterval(async () => {
    if (!generator.id || !hasActiveTasks) return;
    try {
      const summaryResponse = await getTraceSummary({ scenarioId: generator.id });
      setTraceSummary(summaryResponse.counts ?? {});
      setTraceSummaryError(null);
    } catch (error) {
      setTraceSummaryError("Failed to refresh summary.");
    }
  }, 3_000);

  useAsyncEffect(async (signal) => {

    if (!generator.id) return;

    const conversationsResponse = await listConversations({ scenarioId: generator.id, limit: 50 }, signal);

    setTraceConversations(conversationsResponse.items);

  }, [activeTaskIds]);

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  const generatorOptions = useMemo(() =>
    generatorList.map((gen) => ({
      label: gen.name,
      value: gen
    })),
  [generatorList]);

  const [selectedGeneratorOption, setSelectedGeneratorOption] = useState<{ label: string | JSX.Element, value: survey.ConversationGenerator } | undefined>();

  const handleGeneratorSelect = (option: typeof selectedGeneratorOption) => {
    const gen = option?.value ?? EMPTY_GENERATOR;
    distroRef.current?.initialize(
      gen, undefined, undefined, undefined, { generators: generatorReferences }
    );
    setSelectedGeneratorOption(option);
  };

  const handleRegister = async () => {
    const { id } = await registerGenerator({
      id: generator.id,
      config: {
        steps: generator.steps,
        name: generator.name
      },
    });

    distroRef.current?.initialize(
      { ...generator, id }, undefined, undefined, undefined, { generators: generatorReferences }
    );

    const generators = await listGenerators({});

    setGeneratorList(
      generators.map(({ id, config: gen }) => ({
        id,
        name: gen.name!,
        steps: gen.steps
      }))
    );

    setPlan(await getPlan({
      scenarioId: id
    }));

    toast({
      title: 'Updated',
      description: 'Updated config!'
    });
  };

  const handleQueue = async () => {
    if (!generator.id) return;
    setTraceConversationsLoading(true);
    setTraceConversationsError(null);
    setTraceSummaryError(null);
    try {
      const queueResult = await queueTrace({ scenarioId: generator.id });
      const [conversationsResponse, summaryResponse] = await Promise.all([
        listConversations({ scenarioId: generator.id, limit: 50 }),
        getTraceSummary({ scenarioId: generator.id })
      ]);
      setTraceConversations(conversationsResponse.items ?? []);
      setTraceSummary(summaryResponse.counts ?? {});
      if (queueResult?.taskId) {
        addTaskIds([queueResult.taskId]);
      }
      toast({
        title: 'Queued',
        description: `${plan || null} Runs Queued`
      });
    } catch (error) {
      setTraceConversationsError("Failed to queue or refresh conversations.");
      setTraceSummaryError("Failed to refresh summary.");
    } finally {
      setTraceConversationsLoading(false);
    }
  };

  const statusOrder = ["done", "failed", "pending", "ingesting", "unknown"];
  const statusPillClass = (status: string) => {
    switch (status) {
      case "done":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "pending":
      case "ingesting":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const groupField = groupByConfig.enabled && groupByConfig.rules?.length
    ? groupByConfig.rules[0].field
    : null;
  const useRunGrouping = groupField === "run_id";

  const groupByFields = useMemo<FieldOption[]>(() => ([
    { key: "run_id", label: "Run ID", type: "text" },
    { key: "run_status", label: "Status", type: "text" },
    { key: "created_by", label: "Created By", type: "text" }
  ]), []);

  const summaryCount = (status: string) => traceSummary[status] ?? 0;

  const traceConversationColumns = useMemo((): ColumnDef<TraceConversationRow>[] => [
    {
      id: "expander",
      header: "",
      cell: ({ row }) => {
        if (!row.getCanExpand()) {
          return <span className="inline-block w-4" />;
        }
        return (
          <button
            type="button"
            onClick={row.getToggleExpandedHandler()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                row.getToggleExpandedHandler()();
              }
            }}
            className="inline-flex items-center justify-center rounded p-1 text-gray-600 hover:bg-gray-100"
            aria-label={row.getIsExpanded() ? "Collapse run" : "Expand run"}
            aria-expanded={row.getIsExpanded()}
          >
            <Icon
              name={row.getIsExpanded() ? "ChevronDownIcon" : "ChevronRightIcon"}
              size="sm"
              role="decorative"
            />
          </button>
        );
      },
      meta: {
        headerProps: { className: "w-8" },
        cellProps: { className: "w-8" }
      }
    },
    {
      header: "Run ID",
      accessorKey: "run_id",
      cell: ({ row }) =>
        row.original.row_type === "run" || !useRunGrouping ? (
          <span className={`text-sm ${row.original.row_type === "run" ? "font-medium text-gray-900" : "text-gray-900"}`}>
            {row.original.run_id}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )
    },
    {
      header: "Status",
      accessorKey: "run_status",
      cell: ({ row }) =>
        row.original.row_type === "run" || !useRunGrouping ? (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusPillClass(row.original.run_status)}`}
          >
            {row.original.run_status}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )
    },
    {
      header: "Conversation ID",
      accessorKey: "id",
      cell: ({ row }) =>
        row.original.row_type === "conversation" ? (
          <span className="text-sm text-gray-900">{row.original.id}</span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )
    },
    {
      header: "Branch",
      accessorKey: "branch_index",
      cell: ({ row }) =>
        row.original.row_type === "conversation" ? (
          <span className="text-sm text-gray-900">{row.original.branch_index}</span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )
    },
    {
      header: "Created",
      accessorKey: "created_at",
      cell: ({ row }) => (
        row.original.created_at ? (
          <DateDisplay
            utcValue={row.original.created_at}
            className="text-sm"
            optionOverrides={{ dateStyle: "medium", timeStyle: "short", timeZoneName: undefined }}
            displayInUTC={false}
          />
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )
      )
    },
    {
      header: "Updated",
      accessorKey: "updated_at",
      cell: ({ row }) => (
        row.original.updated_at ? (
          <DateDisplay
            utcValue={row.original.updated_at}
            className="text-sm"
            optionOverrides={{ dateStyle: "medium", timeStyle: "short", timeZoneName: undefined }}
            displayInUTC={false}
          />
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )
      )
    },
    {
      header: "Origin ID",
      accessorKey: "origin_id",
      cell: ({ row }) =>
        row.original.row_type === "conversation" ? (
          <span className="text-sm text-gray-900">{row.original.origin_id}</span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )
    },
    {
      header: "Created By",
      accessorKey: "created_by",
      cell: ({ row }) =>
        row.original.row_type === "conversation" ? (
          <span className="text-sm">{row.original.created_by ?? "—"}</span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )
    }
  ], [useRunGrouping]);

  const statusSummary = useMemo(() => {
    const total = traceConversations.length;
    const counts = traceConversations.reduce<Record<string, number>>((acc, row) => {
      const key = row.run_status || "unknown";
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
    return { total, counts };
  }, [traceConversations]);

  const groupedTraceConversations = useMemo(() => {
    const runs = new Map<string, TraceConversationRow>();
    const runLatestCreated = new Map<string, number>();

    traceConversations.forEach((row) => {
      const runId = row.run_id;
      let runRow = runs.get(runId);
      if (!runRow) {
        runRow = {
          id: `run-${runId}`,
          run_id: runId,
          run_status: row.run_status,
          row_type: "run",
          expandableRows: []
        };
        runs.set(runId, runRow);
      }

      runRow.run_status = row.run_status;
      runRow.expandableRows?.push({
        id: row.id,
        origin_id: row.origin_id,
        created_at: row.created_at,
        updated_at: row.updated_at,
        created_by: row.created_by,
        run_id: row.run_id,
        run_status: row.run_status,
        branch_index: row.branch_index,
        row_type: "conversation"
      });

      const createdAt = row.created_at ? new Date(row.created_at).getTime() : 0;
      if (createdAt) {
        runLatestCreated.set(runId, Math.max(runLatestCreated.get(runId) ?? 0, createdAt));
      }
    });

    return Array.from(runs.values()).sort((a, b) => {
      const aTime = runLatestCreated.get(a.run_id) ?? 0;
      const bTime = runLatestCreated.get(b.run_id) ?? 0;
      return bTime - aTime;
    });
  }, [traceConversations]);

  const flatTraceConversations = useMemo(() => (
    traceConversations.map((row) => ({
      ...row,
      row_type: "conversation" as const
    }))
  ), [traceConversations]);
  const tableData = useRunGrouping ? groupedTraceConversations : flatTraceConversations;
  const tableGroupBy = useRunGrouping || !groupByConfig.enabled ? undefined : groupByConfig;
  
  const downloadCSV = async () => {
    try {
      setDownloadInProgress(true);
      const data: any = await getInteractionsCSV({ scenarioId: generator.id! });
      const respJSON = safeParse(data, {});
      if (respJSON?.error) {
        throw new Error(respJSON.error);
      }
      const blob = new Blob([data as any], { type: "text/csv" });      
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      const date = new Date();
      elem.download = `interactions_${generator.name ?? generator.id}_${new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
        .replace('T', '_')
        .replace(/:/g, '-')}`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);      
    }
    catch (err: any) {
      console.error(err);
      toast({
        description: `Error downloading trace interactions: ${err?.message ?? "Unknown error"}`,
        variant: 'error'
      });      
    }
    finally {
      setDownloadInProgress(false);
    }
  };
  
  return (
    <div className="bg-gray-100 h-full p-4">
      <h1>Conversation Generation</h1>

      <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900">Generator Config</h2>
            <p className="text-sm text-gray-500">Load a generator, edit steps, then register and queue conversations.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="mb-3">
              <Select
                label="Generator"
                hideLabel
                description="Generator Selection"
                showDescription
                options={generatorOptions}
                value={selectedGeneratorOption}
                onChange={handleGeneratorSelect}
                clearable
                searchable
                className="w-56"
              />
            </div>
            <Button isDisabled={!generator.name} onClick={handleRegister}>{generator.id ? "Update" : "Register"}</Button>
            <Button onClick={handleQueue} isDisabled={!generator.id || !plan}>Queue {plan ? ` (${plan})` : ''}</Button>
          </div>
        </div>

        <div className="rounded-md border border-gray-200">
          <Suspense fallback={<div className="p-4 text-sm text-gray-500">Loading editor...</div>}>
            <Distro
              ref={distroRef}
              value={generator}
              types="src/survey.ts"
              llmContext={""}
              name="ConversationGenerator"
              onChange={(v) => setGenerator(v)}
              references={{ generators: generatorReferences }}
            />
          </Suspense>
        </div>
      </div>

      <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900">Conversation Batch Status</h2>
            <p className="text-sm text-gray-500">
              {generator.id ? "Live status for runs created by this scenario." : "Load a generator to see run status."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-sm font-medium text-blue-800">
              Pending: {summaryCount("pending")}
            </span>
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-sm font-medium text-amber-800">
              Running: {summaryCount("running")}
            </span>
          </div>
        </div>
        {traceSummaryError && (
          <div className="mt-2 text-sm text-red-600">{traceSummaryError}</div>
        )}
      </div>

      <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Conversations
              {generator.id ? ` (${statusSummary.total})` : ""}
            </h2>
            <p className="text-sm text-gray-500">
              {generator.id ? "Conversations generated for this scenario." : "Load a generator to view recent conversations."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <GroupByPanel
              value={groupByConfig}
              onChange={setGroupByConfig}
              availableFields={groupByFields}
            />
            <Button
              isDisabled={!tableData.length || downloadInProgress}
              icon={<Icon name="ArrowDownTrayIcon" />}
              loading={downloadInProgress}
              onClick={downloadCSV}>
              CSV
            </Button>
          </div>
        </div>

        {traceConversationsError && (
          <div className="text-sm text-red-600">{traceConversationsError}</div>
        )}

        {!!generator.id && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            {statusOrder
              .filter((status) => statusSummary.counts[status])
              .map((status) => (
                <span
                  key={status}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-sm font-medium ${statusPillClass(status)}`}
                >
                  {status}: {statusSummary.counts[status]}
                </span>
              ))}
            {Object.keys(statusSummary.counts)
              .filter((status) => !statusOrder.includes(status))
              .map((status) => (
                <span
                  key={status}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusPillClass(status)}`}
                >
                  {status}: {statusSummary.counts[status]}
                </span>
              ))}
          </div>
        )}

        <DataTable
          columns={traceConversationColumns}
          data={tableData}
          loading={traceConversationsLoading}
          loadingMessage="Loading recent conversations..."
          noDataMessage={generator.id ? "No conversations found for this scenario." : "Select a generator to see conversations."}
          getSubRows={useRunGrouping ? (row) => row.expandableRows ?? [] : undefined}
          getRowId={(row) => row.id}
          groupBy={tableGroupBy}
          actions={[
            {
              label: "Review",
              onClick: (row) => history.push(`/ai/config/review/${row.id}`),
              hideWhen: (row) => row.row_type !== "conversation" || row.run_status === 'ingesting'
            }
          ]}
        />
      </div>
    </div>
  );
}
